import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Container, Typography, makeStyles } from '@material-ui/core'

import ServiceBankCard from '../../../components/Cards/Payments/ServiceBankCard'
import AccountBalanceCard from '../../../components/Cards/Payments/AccountBalanceCard'
import PaymentMethodCard from '../../../components/Cards/Payments/PaymentMethodCard'

import LoadingOverlay from '../../../components/Misc/LoadingOverlay'

import TransactionsTable from '../../../components/Tables/TransactionsTable'
import { getVisitsByHouseholdId, clearHouseholdFilters } from '../../../actions/visits/visits.actions'
import { showError } from '../../../actions/misc/misc.actions'
import { groupBy } from 'lodash'
import moment from 'moment'

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1,
		paddingLeft: '0px',
		paddingRight: '0px',
		paddingBottom: '30px'
	},
	paymentsWrapper: {
		paddingTop: '24px',
		paddingLeft: '20px',
		paddingRight: '20px'
	},
	wrapper: {
		display: 'flex'
	}
}))

const Payments = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const selected = useSelector((state) => state.specific.households.selected)
	const facilities = useSelector((state) => state.specific.facilities.facilities)
	const household = useSelector((state) => state.specific.households.selected)
	const visits = useSelector((state) => state.specific.visits.visits)
	const loading = useSelector((state) => state.specific.households.loading)
	const error = useSelector((state) => state.specific.households.error)
	const [transactions, setTransactions] = useState([])
	const [defaultFacility, setDefaultFacility] = useState(null)

	useEffect(() => {
		if (error) {
			dispatch(showError(error))
		}
	}, [error])

	useEffect(() => {
		if (facilities && facilities.length > 0 && selected) {
			setDefaultFacility(facilities.find((facility) => String(facility._id) === String(selected.default_facility_id)) || null)
		}
	}, [facilities, selected])

	useEffect(() => {
		if (selected && selected.transactions) {
			let transactions = selected.transactions.map(t => {
				if (t.type === 'member_dues') {
					let name = selected.accounts.find(a => a._id === t.account_id)
					return {
						...t,
						title: t.description,
						description: name ? `Dues for ${name.first_name} ${name.last_name}` : ''
					}
				}

				if (t.type === 'daycare_promo_credit' || t.type === 'credits_used') {
					return {
						...t,
						stripe_charge_status: 'succeeded'
					}
				}

				return t
			})
			let credits = [...selected.credits.daycare, ...selected.credits.walk] //daycare , walk, playcare
			let expiredCredits = credits
				.filter((c) => c.expired)
				.map((c) => ({
					...c,
					formattedExpDate: c.expiration_date
						? moment(c.expiration_date).format('MMM DD, YYYY')
						: moment(c.createdAt).add(1, 'year').format('MMM DD, YYYY')
				}))

			expiredCredits = groupBy(expiredCredits, 'type') //[{daycare : [{}...{}]}, {walk : [...]}]

			Object.keys(expiredCredits).forEach(
				(creditType) =>
					(expiredCredits[creditType] = groupBy(expiredCredits[creditType], 'formattedExpDate'))
			)

			let expiredCreditTransactions = []

			Object.keys(expiredCredits).forEach((type) => {
				Object.keys(expiredCredits[type]).forEach((date) => {
					let expCredits = expiredCredits[type][date]

					if (type === 'daycare') {
						expCredits = groupBy(expCredits, 'facility_id') // {facility_id : [], }
						Object.keys(expCredits).forEach((facility) => {
							let credits = expCredits[facility]
							expiredCreditTransactions.push({
								createdAt: moment(date).format(),
								title: `${credits.length} Credit${credits.length !== 1 ? 's' : ''
									} Expired - Daycare${getFacilityName(facility)}`,
								description: credits[0].description || ''
							})
						})
					} else {
						expiredCreditTransactions.push({
							createdAt: moment(date).format(),
							title: `${expCredits.length} Credit${expCredits.length !== 1 ? 's' : ''
								} Expired - ${type} Minute`,
							description: expCredits[0].description || ''
						})
					}
				})
			})

			// this section groups the transactions by visit, when applicable, and overwrites the createdAt for certain
			// line items that would normally appear outside the visit dates (early dropoff, etc) so they are grouped by a sensible date in the table.
			// Requires the visit list to run, so we fall back to the normal sort if that isn't available yet
			let mapped_transactions = []
			if (!visits?.length) {
				setTransactions([...transactions, ...expiredCreditTransactions].sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt))))
			} else {

				let hasVisit = []
				let newArr = []
				// separate line items that go with a visit
				transactions.forEach(t => {
					if (t.visit_id) {
						hasVisit.push(t)
					} else {
						newArr.push(t)
					}
				})

				let grouped = groupBy(hasVisit, 'visit_id')

				// overwrite certain createdAt dates, and insert the group of charges into the main list 
				// according to the overall startDate of the visit
				for (let id in grouped) {
					let vis = visits.find(v => v._id == id)
					if (vis) {
						let createdAt = moment(vis.startDate).toDate()
						let newOnes = grouped[id].map(t => {
							let created = t.createdAt
							if (t.type === 'early_dropoff') created = moment(vis.startDate).toDate()
							if (t.type === 'extended_pickup') created = moment(vis.endDate).toDate()
							if (t.type === 'deposit') created = moment(vis.startDate).toDate()
							return {
								...t,
								createdAt: created
							}
						})
						newArr.push({ createdAt, entries: newOnes })
					}
				}

				// sort the main list 
				const sorted = [...newArr, ...expiredCreditTransactions].sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))

				// unwind the grouped charges, which should now be sorted together in the list
				sorted.forEach(s => {
					if (s.entries) {

						let newEntries = s.entries.map((e, i) => {
							const obj = {
								...e,
								isGrouped: true
							}

							if (i === 0) {
								obj.isFirst = true
							}

							if (i === s.entries.length - 1) {
								obj.isLast = true
							}

							return obj

						})

						let mainCharge = newEntries.find(e => e.type === 'boarding' || e.type === 'daycare' || e.type === 'grooming')
						if (mainCharge) {
							mainCharge.isMain = true
						} else {
							mainCharge = newEntries.find(e => e.amount > 0)
							if (mainCharge) mainCharge.isMain = true
						}

						mapped_transactions.push(...newEntries)
					} else {
						mapped_transactions.push(s)
					}
				})

				setTransactions(mapped_transactions)
			}


		}
	}, [selected, visits])

	const getFacilityName = (facility_id) => {
		let found = facilities.find((f) => f._id === facility_id)
		return found ? ` ${found.name}` : ''
	}

	return (
		<Container className={classes.root} maxWidth={false}>
			<div className={classes.paymentsWrapper}>
				<Typography variant="h5" style={{ fontWeight: 800, marginBottom: 24 }}>
					Payments
				</Typography>
				<div className={classes.wrapper}>
					<div style={{ flex: 1, marginRight: 24 }}>
						<ServiceBankCard household={selected} />
						<AccountBalanceCard household={selected} />
					</div>
					<div style={{ width: 369 }}>
						<PaymentMethodCard household={selected} defaultFacility={defaultFacility} />
					</div>
				</div>
			</div>
			<TransactionsTable data={transactions} household={selected} />
			{loading ? <LoadingOverlay /> : null}
		</Container>
	)
}

export default Payments
